import * as Sentry from "@sentry/react";
import { getCustomers } from "./Api";
import { LocalStorageVariables } from "../entities/type";
import { ClientType } from "../entities/profile";
import {
  getLastKnownIdentity,
  identityFromUser,
} from "../utils/affiliateIdentity";

// Only report to Sentry on production. Dev/staging/local just log to console.
const PROD_HOST = "welcome.mypnoe.com";

type ParsedCustomers = { list: ClientType[]; totalCount: number };

// GetCustomers returns a paginated envelope { totalCount, pageCount, currentPage,
// rows, data }. Older/edge responses may be a bare array. Read defensively.
const parseCustomers = (raw: any): ParsedCustomers => {
  const list: ClientType[] = Array.isArray(raw)
    ? raw
    : raw && Array.isArray(raw.data)
    ? raw.data
    : [];
  const totalCount =
    raw && typeof raw.totalCount === "number" ? raw.totalCount : list.length;
  return { list, totalCount };
};

// Best-effort cache signal from the Resource Timing API. transferSize === 0 with a
// non-zero body usually means the response was served from cache; deliveryType is
// "cache" on newer Chromium. Helps disambiguate cached-empty-200 vs server-side.
const cacheSignal = (): Record<string, any> => {
  try {
    const entries = performance
      .getEntriesByType("resource")
      .filter((e) => e.name.includes("GetCustomers")) as PerformanceResourceTiming[];
    const last = entries[entries.length - 1];
    if (!last) return {};
    return {
      transferSize: last.transferSize,
      encodedBodySize: last.encodedBodySize,
      deliveryType: (last as any).deliveryType ?? null,
    };
  } catch {
    return {};
  }
};

// PII-free anomaly report. Never throws — diagnostics must not break the app.
export const reportClientListAnomaly = (payload: Record<string, any>): void => {
  try {
    if (window.location.hostname !== PROD_HOST) {
      // eslint-disable-next-line no-console
      console.warn("[client-list anomaly]", payload);
      return;
    }
    const usr = JSON.parse(
      localStorage.getItem(LocalStorageVariables.USER) || "null"
    );
    // A dead session is cleared from localStorage before the rejection reaches
    // this reporter, so fall back to the identity recorded on the way in.
    const identity = usr ? identityFromUser(usr) : getLastKnownIdentity();
    const enriched = {
      env: "prod",
      pnoe_user_id: usr?.pnoe_user_id ?? identity?.id,
      role: usr?.role,
      identitySource: usr ? "live" : identity ? "remembered" : "none",
      connection: (navigator as any)?.connection?.effectiveType ?? null,
      ...cacheSignal(),
      ...payload,
    };
    // Must be captureException, not captureMessage: Replay's on-error buffer only
    // flushes for exception-type events — message events never trigger it, at any
    // level. The explicit fingerprint keeps grouping off the synthetic Error's
    // stack trace, but includes anomalyType so the distinct failure modes (empty
    // 200 vs 401) land in separate issues instead of sharing one Resolve button.
    Sentry.captureException(new Error("client-list-empty"), {
      level: "error",
      fingerprint: ["client-list-empty", String(payload.anomalyType ?? "unknown")],
      // Affiliate account identity (searchable as user.email in Sentry) — the
      // affiliate's own login, not client/patient data. Omitted entirely when
      // unknown, so it can't overwrite an identity already on the Sentry scope.
      ...(identity?.id || identity?.email ? { user: identity } : {}),
      extra: enriched,
    });
  } catch {
    /* swallow — diagnostics must never break the app */
  }
};

export type ResilientCustomers = { list: ClientType[]; totalCount: number };

/**
 * Fetches the affiliate client list, retrying once with a cache-busting request
 * when the first response is empty. That single retry both *recovers* users hit by
 * a cached/stale empty 200 and *diagnoses* the cause (a cache-busted refetch that
 * returns data ⇒ it was a cache; still empty ⇒ server-side).
 *
 * Throws when the backend reports clients exist (totalCount > 0) yet keeps
 * returning none, so callers can show an error/retry state instead of a
 * misleading "No clients found". Resolves with an empty list only when the account
 * genuinely has no clients (totalCount === 0).
 */
export const fetchClientsResilient = async (): Promise<ResilientCustomers> => {
  let callCount = 1;
  const first = parseCustomers(await getCustomers());

  if (first.list.length > 0) {
    return { list: first.list, totalCount: first.totalCount };
  }

  // Empty first response — retry once, bypassing any HTTP/proxy/browser cache.
  callCount += 1;
  const retry = parseCustomers(await getCustomers({ bustCache: true }));

  reportClientListAnomaly({
    anomalyType: first.totalCount > 0 ? "empty-nonzero-total" : "empty",
    status: 200,
    totalCount: first.totalCount,
    dataLen: 0,
    reloadLen: retry.list.length,
    getCustomersCallCount: callCount,
  });

  if (retry.list.length > 0) {
    // Recovered — the first empty response was stale/cached.
    return { list: retry.list, totalCount: retry.totalCount };
  }

  // Still empty after a fresh fetch. If the backend claims clients exist, this is a
  // real failure — surface it as an error rather than "No clients found".
  if (first.totalCount > 0 || retry.totalCount > 0) {
    const err: any = new Error("client-list-empty-inconsistent");
    err.__reported = true; // already reported above; avoid a duplicate event
    throw err;
  }

  // Genuinely no clients on this account.
  return { list: [], totalCount: 0 };
};
